import * as Sentry from '@sentry/react'
import { wrapTablesWithDiv } from '@/chat-common/util/source-html-editing-functions'
import { RenderFormat } from '@/content-viewer/store/schemas'
import { convertMarkdownToHtml } from '@/content-viewer/util/markdown-to-html'
import { kGcsUserFilesPipelineBucketRef } from '@/constants/constants-gcs'
import { MimeType } from '@/constants/constants-mime-types'
import { usesStorageEmulator } from '@/firebase/storage-routing'
import { convertReferenceMarkdownToHtml } from '@/reference-highlighting/reference-html'
import { getUserFileViewableUrl, type UserFileViewableUrlResponse } from '../fetch/get-user-file-viewable-url'

type LoadUserFilePdfArgs = {
  userFileId: string
  title?: string
}

type LoadUserFileMarkdownArgs = {
  userFileId: string
  citationHighlightsImprovements: boolean
}

const MARKDOWN_CONTENT_TYPES = new Set<string>([MimeType.PLAIN_TEXT, MimeType.MARKDOWN, MimeType.HTML])

export type UserFileViewableContentResult = { kind: 'pdf'; pdfFile: File } | { kind: 'markdown'; htmlString: string }

export async function loadUserFilePdf(args: LoadUserFilePdfArgs): Promise<File> {
  const { userFileId, title } = args
  const { blob, capability } = await fetchUserFileViewableBlob(userFileId)

  if (capability.content_type !== MimeType.PDF) {
    throw new Error(`User-file viewable content is ${capability.content_type}, not ${RenderFormat.PDF}`)
  }

  return pdfFileFromBlob({ blob, capability, userFileId, title })
}

export async function loadUserFileMarkdownHtml(args: LoadUserFileMarkdownArgs): Promise<string> {
  const { userFileId, citationHighlightsImprovements } = args
  const { blob, capability } = await fetchUserFileViewableBlob(userFileId)

  if (!MARKDOWN_CONTENT_TYPES.has(capability.content_type)) {
    throw new Error(`User-file viewable content is ${capability.content_type}, not ${RenderFormat.MARKDOWN}`)
  }

  return markdownHtmlFromBlob({ blob, capability, userFileId, citationHighlightsImprovements })
}

/**
 * Load a user file's viewable content when the render format is not known up front,
 * branching on the content type the viewable-url endpoint resolves server-side.
 */
export async function loadUserFileViewableContent(args: {
  userFileId: string
  title?: string
  citationHighlightsImprovements: boolean
}): Promise<UserFileViewableContentResult> {
  const { userFileId, title, citationHighlightsImprovements } = args
  const { blob, capability } = await fetchUserFileViewableBlob(userFileId)

  if (capability.content_type === MimeType.PDF) {
    return { kind: 'pdf', pdfFile: pdfFileFromBlob({ blob, capability, userFileId, title }) }
  }
  if (MARKDOWN_CONTENT_TYPES.has(capability.content_type)) {
    return { kind: 'markdown', htmlString: await markdownHtmlFromBlob({ blob, capability, userFileId, citationHighlightsImprovements }) }
  }
  throw new Error(`User-file viewable content type ${capability.content_type} is not renderable`)
}

function pdfFileFromBlob(args: { blob: Blob; capability: UserFileViewableUrlResponse; userFileId: string; title?: string }): File {
  const { blob, capability, userFileId, title } = args
  validateBlobType({
    blobType: blob.type,
    allowedTypes: new Set([MimeType.PDF]),
    errorMessage: 'User-file PDF blob is not application/pdf',
    sentryContext: {
      userFileId,
      title,
      capabilityContentType: capability.content_type,
    },
  })

  return new File([blob], title || `${userFileId}.pdf`, { type: MimeType.PDF })
}

async function markdownHtmlFromBlob(args: {
  blob: Blob
  capability: UserFileViewableUrlResponse
  userFileId: string
  citationHighlightsImprovements: boolean
}): Promise<string> {
  const { blob, capability, userFileId, citationHighlightsImprovements } = args
  validateBlobType({
    blobType: blob.type,
    allowedTypes: MARKDOWN_CONTENT_TYPES,
    errorMessage: `Invalid blob type for user-file markdown fetch: ${blob.type}`,
    sentryContext: {
      userFileId,
      capabilityContentType: capability.content_type,
    },
  })

  const markdown = await readBlobText(blob)
  const html = citationHighlightsImprovements ? await convertReferenceMarkdownToHtml(markdown) : await convertMarkdownToHtml(markdown)
  return wrapTablesWithDiv(html)
}

async function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') {
    return blob.text()
  }
  return new Response(blob).text()
}

async function fetchUserFileViewableBlob(userFileId: string): Promise<{ blob: Blob; capability: UserFileViewableUrlResponse }> {
  const capability = await getUserFileViewableUrl(userFileId)
  const response = await fetch(capability.viewable_url)
  if (!response.ok) {
    throw new Error(`Viewable content fetch failed with status ${response.status}`)
  }
  return {
    blob: await response.blob(),
    capability,
  }
}

function validateBlobType(args: { blobType: string; allowedTypes: Set<string>; errorMessage: string; sentryContext: Record<string, unknown> }) {
  const { blobType, allowedTypes, errorMessage, sentryContext } = args
  if (!blobType || allowedTypes.has(blobType)) return

  const allowEmulatorOctetStream = usesStorageEmulator({ bucketUrl: kGcsUserFilesPipelineBucketRef }) && blobType === MimeType.OCTET_STREAM
  if (allowEmulatorOctetStream) return

  Sentry.captureException(new Error(errorMessage), {
    extra: { ...sentryContext, blobType },
  })
  throw new Error(errorMessage)
}
